import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { notify } from '../../utils/notifications';
import { X, Percent, Euro, Tag } from 'lucide-react';

/**
 * Sconto sull'abbonamento: in percentuale o come importo fisso sull'anno.
 *
 * Lo sconto entra nel calcolo delle quote, quindi si ricalcolano solo le
 * mensilità non ancora incassate: quelle già pagate restano al valore con
 * cui sono state saldate.
 */
const ModalSconto = ({ abbonamento, onClose, onApplicato }) => {
  const [tipo, setTipo] = useState('PERCENTUALE');
  const [valore, setValore] = useState('');
  const [motivo, setMotivo] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);

  useEffect(() => {
    if (abbonamento) {
      const attuale = abbonamento.sconto_tipo && abbonamento.sconto_tipo !== 'NESSUNO';
      setTipo(attuale ? abbonamento.sconto_tipo : 'PERCENTUALE');
      setValore(attuale ? String(Number(abbonamento.sconto_valore)) : '');
      setMotivo(abbonamento.sconto_motivo || '');
    }
  }, [abbonamento]);

  if (!abbonamento) return null;

  const scontoAttivo = abbonamento.sconto_tipo && abbonamento.sconto_tipo !== 'NESSUNO';

  // Anteprima del totale, calcolata solo sulle mensilità ancora aperte
  const rate = abbonamento.rate || [];
  const daIncassare = rate.filter((r) => !r.pagata && !r.gratuita);
  const totaleAperto = daIncassare.reduce((a, r) => a + Number(r.importo), 0);
  const pieno = Number(abbonamento.totale_senza_sconto || 0);
  const n = Number(valore) || 0;

  let scontoStimato = 0;
  if (tipo === 'PERCENTUALE') scontoStimato = (totaleAperto * n) / 100;
  else scontoStimato = Math.min(n, totaleAperto);

  const invia = async (rimuovi = false) => {
    setSalvataggio(true);
    const toastId = notify.loading(rimuovi ? 'Rimozione sconto...' : 'Applicazione sconto...');
    try {
      const res = await api.post(`/abbonamenti/abbonamenti/${abbonamento.id}/applica_sconto/`, {
        tipo: rimuovi ? 'NESSUNO' : tipo,
        valore: rimuovi ? 0 : valore,
        motivo,
      });
      notify.update(toastId, { type: 'success', render: res.data.messaggio });
      onClose();
      onApplicato?.();
    } catch (error) {
      notify.update(toastId, {
        type: 'error',
        render: error.response?.data?.errore || 'Errore nell\'applicazione dello sconto',
      });
    } finally {
      setSalvataggio(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
        <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Tag className="w-4 h-4 text-[#1b94ce]" />
            Applica scontistica
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); invia(false); }} className="p-5 space-y-4">
          {scontoAttivo && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-900">
                Sconto attualmente applicato: <strong>{abbonamento.sconto_descrizione}</strong>
              </p>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Tipo di sconto *</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setTipo('PERCENTUALE')}
                className={`px-3 py-2.5 text-sm rounded-lg border transition font-medium flex items-center justify-center gap-2 ${
                  tipo === 'PERCENTUALE'
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-gray-900'
                }`}
              >
                <Percent className="w-4 h-4" /> Percentuale
              </button>
              <button
                type="button"
                onClick={() => setTipo('IMPORTO')}
                className={`px-3 py-2.5 text-sm rounded-lg border transition font-medium flex items-center justify-center gap-2 ${
                  tipo === 'IMPORTO'
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-gray-900'
                }`}
              >
                <Euro className="w-4 h-4" /> Importo
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              {tipo === 'PERCENTUALE' ? 'Percentuale di sconto (%) *' : 'Importo da scontare sull\'anno (€) *'}
            </label>
            <input
              type="number"
              required
              min="0"
              max={tipo === 'PERCENTUALE' ? 100 : undefined}
              step={tipo === 'PERCENTUALE' ? 1 : 0.01}
              value={valore}
              onChange={(e) => setValore(e.target.value)}
              placeholder={tipo === 'PERCENTUALE' ? 'Es. 10' : 'Es. 50.00'}
              className="w-full px-3 py-2 text-lg font-bold border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Motivo</label>
            <input
              type="text"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Es. secondo figlio, convenzione"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
            />
          </div>

          {n > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
              <p className="text-xs text-gray-600">
                Quota piena dell'anno: <strong>€{pieno.toFixed(2)}</strong>
              </p>
              <p className="text-xs text-gray-600">
                Ancora da incassare: <strong>€{totaleAperto.toFixed(2)}</strong> su {daIncassare.length} mensilità
              </p>
              <p className="text-sm text-gray-900 pt-1 border-t border-gray-200">
                Sconto stimato: <strong>−€{scontoStimato.toFixed(2)}</strong>
              </p>
              <p className="text-[11px] text-amber-700">
                Lo sconto tocca solo le mensilità non ancora incassate: quelle già
                pagate restano al valore con cui sono state saldate.
              </p>
            </div>
          )}

          <div className="flex justify-between items-center gap-3 pt-3 border-t border-gray-200">
            {scontoAttivo ? (
              <button
                type="button"
                onClick={() => invia(true)}
                disabled={salvataggio}
                className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition font-medium disabled:opacity-40"
              >
                Rimuovi sconto
              </button>
            ) : <span />}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold text-sm"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={salvataggio || !valore || Number(valore) <= 0}
                className="px-5 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition font-semibold text-sm disabled:opacity-40"
              >
                {salvataggio ? 'Applicazione...' : 'Applica'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalSconto;
